import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ticketDeviceTabQuery } from '../api/easeedesk'
import { DeviceLivePanel } from '../devices/DeviceLivePanel'
import { renderScreen } from '../devices/screens'
import { Spinner } from '../ui/Spinner'
import './TicketDeviceTab.css'
import { NEW_TAB } from '../ui/NewTab'

/**
 * Filele de echipament ale fisei - `Easeedesk::tab($tab, $ticket)` cu machetele
 * `easeedesk/device/<tab>Tab.php`.
 *
 * Toate patru - Live, Health, Software, Hardware - desenau acelasi rand de
 * butoane, cate unul pe masina atasata tichetului, si se deosebeau numai prin
 * panoul pe care il deschidea butonul. Deci randul se scrie o data, si fiecare
 * fila ii da panoul ei.
 *
 * PANOUL E CEL AL MODULULUI, NU O A DOUA COPIE A LUI.
 * ---------------------------------------------------------------------------
 *
 * In portal, fiecare din cele patru file deschidea o macheta a EI:
 * `inventoree/device/quickDetails.php`, `softsee/device/quickDetails.php`,
 * `opteemum/device/quickDetails.php` - toate trei citind aceleasi tabele ca
 * ecranele de rezumat ale modulelor, si aratand cam aceleasi cifre, altfel
 * asezate.
 *
 * "Cam aceleasi" e chiar problema. `softsee/device/quickDetails.php` cere azi
 * `$softwareChart` si `$eventsChart`, pe care `Easeedesk::deviceQuickDetails()`
 * nu i le da: macheta a fost mutata pe widgeturi odata cu ecranul modulului, si
 * copia de pe tichet a ramas in urma. Doua transcrieri ale acelorasi numere se
 * despart, si a doua se strica tacut.
 *
 * Deci filele deschid aici REZUMATUL MODULULUI, cel deja mutat si probat:
 * `inventoree/deviceQuickDetails/` pentru Hardware, `softsee/deviceSummary/`
 * pentru Software, `opteemum/deviceSummary/` pentru Health. Aceleasi date, un
 * singur loc in care se schimba.
 *
 * "LIVE" E ALTFEL, si de-aia nu e in `PANELS`.
 * ---------------------------------------------------------------------------
 *
 * Celelalte trei file deschid rezumatul unui modul, adica un ecran care se
 * citeste dintr-o baza. Cele sase cadrane ale filei Live nu se citesc de
 * nicaieri: vin in timp real de pe masina, prin socket, la trei secunde
 * (`ioSocket.requestPerformance/Diskspace/Processes`). Nu e un rezumat de modul
 * si nu are cheie in meniul fisei, deci nu trece prin `renderScreen` - se
 * deseneaza de-a dreptul, cu `DeviceLivePanel`.
 *
 * Panoul e in `src/devices/`, nu aici: fisa unui aparat si tabloul de alarme se
 * uita la aceleasi cifre, si nu are de ce sa fie al lui Easeedesk fiindca a fost
 * primul care l-a cerut.
 */

/**
 * Panoul fiecarei file, pe cheia ecranului de aparat care il deseneaza.
 * Ce nu e aici inca nu se poate arata.
 */
const PANELS: Record<string, { href: string; label: string }> = {
  hardware: { href: 'inventoree/deviceQuickDetails/', label: 'Hardware summary' },
  /*
   * Softsee::deviceQuickDetails, nu fila Summary a modulului: fereastra rapida
   * are lista CVE, suspectele, ultimele evenimente si nu cere drept de Softsee -
   * un agent de helpdesk o vede din tichet, ca in portal (`$ignorePages`).
   */
  software: { href: 'softsee/deviceQuickDetails/', label: 'Software summary' },
  health: { href: 'opteemum/deviceSummary/', label: 'Health summary' },
}

export function TicketDeviceTab({ id, tab }: { id: number; tab: string }) {
  const { data, error } = useQuery(ticketDeviceTabQuery(id, tab))
  const [open, setOpen] = useState(0)

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  if (data.devices.length === 0) {
    /* Portalul nu desena nimic aici - `if ($devices)` in jurul machetei - si
       fila ramanea alba. O fila alba si una pe o masina linistita arata la fel,
       si sunt doua lucruri diferite. */
    return (
      <div className="alert alert-info" role="alert">
        <i className="fas fa-info-circle"></i> No device is attached to this ticket.
      </div>
    )
  }

  const panel = PANELS[tab]
  const device = data.devices[Math.min(open, data.devices.length - 1)]

  return (
    <div id={`dt-${tab}-${id}`}>
      <ul className="nav nav-tabs nav-tabs-secondary dt-devs" role="tablist">
        {data.devices.map((d, i) => (
          <li key={d.id} role="presentation" className={i === open ? 'active' : undefined}>
            <a onClick={() => setOpen(i)}>
              <i className="fas fa-desktop"></i> {d.name}
            </a>
          </li>
        ))}
      </ul>

      <div className="tab-content tab-content-secondary dt-panel">
        {tab === 'live' ? (
          <DeviceLivePanel id={device.id} />
        ) : panel ? (
          renderScreen(panel.href, panel.label, { id: device.id, from: 'easeedesk', overview: [], open: () => undefined })
        ) : (
          <div className="alert alert-info" role="alert">
            <i className="fas fa-info-circle" aria-hidden="true"></i> This panel still opens in the current portal:{' '}
            <a href={`/easeedesk/tab/${tab}/${id}`} {...NEW_TAB}>/easeedesk/tab/{tab}/{id}</a>
          </div>
        )}
      </div>
    </div>
  )
}
